import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import api from "../lib/api";
import {
  Sparkle, Calculator, ArrowLeft, Coins, ChartLineUp,
  Warning, Crown, Lightning, Network, Lock, Star,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import PaywallGate from "../components/PaywallGate";
import KundaliChart from "../components/KundaliChart";
import usePrefillBirthDetails from "../hooks/usePrefillBirthDetails";
import { useSavedReportLoader } from "../lib/useSavedReport";
import PlaceAutocompleteInput from "../components/PlaceAutocompleteInput";
import { useAuth } from "../context/AuthContext";
import { useToolUnlock } from "../lib/toolUnlock";

const PLANET_TINT = {
  Sun: "amber", Moon: "slate", Mars: "rose", Mercury: "emerald",
  Jupiter: "yellow", Venus: "pink", Saturn: "indigo", Rahu: "violet", Ketu: "fuchsia",
};

const DIGNITY_CHIP = {
  exalted:     { label: "Exalted",     cls: "bg-amber-200 text-amber-900" },
  mooltrikona: { label: "Mooltrikona", cls: "bg-amber-200 text-amber-900" },
  own_sign:    { label: "Own sign",    cls: "bg-emerald-200 text-emerald-900" },
  debilitated: { label: "Debilitated", cls: "bg-rose-200 text-rose-900" },
  neutral:     { label: "Neutral",     cls: "bg-stone-200 text-stone-700" },
};

const BAND_TINT = {
  strong:   { bg: "bg-emerald-50 border-emerald-300", text: "text-emerald-900", chip: "bg-emerald-200 text-emerald-900" },
  moderate: { bg: "bg-amber-50 border-amber-300",     text: "text-amber-900",   chip: "bg-amber-200 text-amber-900" },
  weak:     { bg: "bg-rose-50 border-rose-300",       text: "text-rose-900",    chip: "bg-rose-200 text-rose-900" },
};

export default function RudramshaPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ date: "", time: "", place: "", timezone: "Asia/Kolkata" });
  usePrefillBirthDetails(setForm);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [narrative, setNarrative] = useState(null);
  const [narrativeBusy, setNarrativeBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Flips true the moment this user has paid (or is already unlocked) on
  // this chart. Used to decide whether to push a follow-up
  // POST /api/reports/save once the Claude narrative lands so the saved
  // copy in "My Reports" includes the personalised text.
  const [unlocked, setUnlocked] = useState(false);
  const { user } = useAuth();

  // Server-side paywall now guards `/rudramsha/narrative` (Universal-Key
  // spend). We mirror that check here so we NEVER compute or hit the
  // narrative endpoint pre-payment — "no teasers" per product decision
  // (2026-07-28).
  const hasBirth = !!(form.date && form.time && form.place);
  const { unlocked: alreadyUnlocked } = useToolUnlock({
    tool: "rudramsha",
    birthDetails: hasBirth ? form : null,
    user,
  });
  // Merge both signals — `unlocked` (local, flipped on payment success)
  // and `alreadyUnlocked` (from useToolUnlock, true on returning paid
  // users). Either one means we can render.
  const canView = unlocked || alreadyUnlocked;

  useSavedReportLoader({ expectedTool: "rudramsha", setForm, setResult });

  // When a saved report is hydrated (or when we merge narrative into
  // result), pluck the persisted narrative out so PremiumNarrative
  // doesn't spin Claude up again.
  React.useEffect(() => {
    if (result?.narrative && !narrative) setNarrative(result.narrative);
  }, [result, narrative]);

  // Fire-and-forget save once we have ALL three: signed-in unlock,
  // computed chart, and Claude narrative. Idempotent on the backend.
  React.useEffect(() => {
    if (!unlocked || !result || !narrative) return;
    if (result.narrative === narrative) return; // already saved
    const merged = { ...result, narrative };
    setResult(merged);
    api.post("/reports/save", {
      tool: "rudramsha",
      birth_details: form,
      report: merged,
    }).catch(() => { /* best-effort */ });
  }, [unlocked, narrative]);

  const handle = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const runCompute = async () => {
    if (busy || result) return;
    setBusy(true);
    setNarrative(null);
    try {
      const { data } = await api.post("/rudramsha/compute", form);
      setResult(data);
      setTimeout(() => {
        document.querySelector("[data-testid='rudramsha-result']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
      // Now that compute succeeded AND we know the user is unlocked, kick
      // the Claude narrative in the background.
      fetchNarrativeFor(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not compute your Rudramsha. Please re-check your inputs.");
    } finally {
      setBusy(false);
    }
  };

  // Compute only after unlock — no math, no LLM before payment.
  useEffect(() => {
    if (submitted && canView && !result && !busy) runCompute();
  }, [submitted, canView]);

  const analyze = (e) => {
    e?.preventDefault();
    if (!form.date || !form.time || !form.place) {
      toast.error("Please enter your date, time and place of birth.");
      return;
    }
    setSubmitted(true);
  };

  // Called after unlock — fires Claude narrative endpoint. Sends
  // birth_details so backend can verify the unlock server-side.
  const fetchNarrativeFor = async (computed) => {
    if (!computed || narrativeBusy) return;
    setNarrativeBusy(true);
    try {
      const { data } = await api.post("/rudramsha/narrative", {
        report: computed,
        birth_details: form,
      });
      setNarrative(data?.narrative || null);
    } catch (err) {
      // Silently swallow — the page is still useful with just the chart,
      // and PremiumNarrative falls back to a templated reading on retry.
    } finally {
      setNarrativeBusy(false);
    }
  };

  const fetchNarrative = () => fetchNarrativeFor(result);

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="rudramsha-page">
      <Header />
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-1 text-sm text-muted mb-4"
          data-testid="rudramsha-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="flex items-center gap-2 text-gold label-eyebrow">
          <Coins size={14} weight="duotone" /> Premium · D11 Rudramsha · Wealth & Gains
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 font-light leading-tight">
          Rudramsha <span className="italic text-gold">D11</span> · Wealth Calculator
        </h1>
        <p className="text-muted text-sm sm:text-base mt-4 max-w-3xl font-light">
          The Rudramsha (Ekadashamsa / Labhamsha) is the divisional chart of <b className="text-[#3F2D1B]">gains, large income, fulfilled desires and network reach</b>. Each rashi splits into 11 equal segments of 2°43&apos;38&quot; — the resulting chart reveals which marketplace streams your karma is actually built to monetise, layered with the 2/5/9/11 wealth-axis matrix from your D1.
        </p>
        <p className="text-xs text-muted mt-2 max-w-3xl">
          Standard Parashari variant — counting starts from Aries for every sign. Cross-references your natal 11th house, the lord of that house, your current Vimshottari Mahadasha, and the 4 wealth-axis houses (2 hoard · 5 speculation · 9 fortune · 11 gains).
        </p>

        {/* Form */}
        <form onSubmit={analyze} className="mt-8 card-surface rounded-md p-5 sm:p-7 space-y-4 max-w-3xl">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="label-eyebrow text-xs">Date of birth</span>
              <input
                type="date" required value={form.date} onChange={handle("date")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="rudramsha-date"
              />
            </label>
            <label className="block">
              <span className="label-eyebrow text-xs">Time of birth</span>
              <input
                type="time" required value={form.time} onChange={handle("time")}
                className="mt-1.5 w-full px-3 py-2.5 rounded-md border border-soft bg-bg"
                data-testid="rudramsha-time"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="label-eyebrow text-xs">Place of birth</span>
              <div className="mt-1.5">
                <PlaceAutocompleteInput
                  value={form.place}
                  onChange={(v) => setForm((f) => ({ ...f, place: v }))}
                  onPick={({ name, timezone }) =>
                    setForm((f) => ({ ...f, place: name, timezone: timezone || f.timezone }))
                  }
                  placeholder="Mumbai, India"
                  testId="rudramsha-place"
                />
              </div>
            </label>
          </div>
          <button
            type="submit" disabled={busy}
            className="btn-primary rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60"
            data-testid="rudramsha-submit"
          >
            <Calculator size={16} weight="duotone" className="inline mr-2" />
            {busy ? "Reading your Rudramsha…" : "Reveal my Wealth Engine"}
          </button>
        </form>

        {/* Standalone paywall — submitted but not yet unlocked. Nothing is
            computed or shown until payment lands (no teasers, no LLM
            spend). */}
        {submitted && !canView && (
          <div className="mt-10" data-testid="rudramsha-paywall-only">
            <PaywallGate
              tool="rudramsha"
              toolLabel="Rudramsha · D11 Wealth & Gains"
              birthDetails={form}
              variant="standalone"
              onUnlocked={() => {
                setUnlocked(true);
                toast.success("Unlocked! Saved to My Reports.", {
                  description: "Tap here if you ever lose this page — your full reading lives there forever.",
                  action: { label: "View", onClick: () => navigate("/reports") },
                  duration: 8000,
                });
              }}
            />
          </div>
        )}

        {/* Loading state — after unlock, before compute finishes */}
        {submitted && canView && busy && !result && (
          <div className="mt-10 text-center animate-pulse text-muted text-sm" data-testid="rudramsha-loading">
            Reading your D11 (Rudramsha) chart…
          </div>
        )}

        {/* Result — only when unlocked & compute finished */}
        {result && result.d11 && result.d11_lagna_flavour && result.wealth_axis && result.eleventh_house && result.current_dasha && canView && (
          <div className="mt-10 space-y-7" data-testid="rudramsha-result">
              <div className="space-y-7">
                {/* === HERO: D11 Lagna flavour + chart === */}
                <section className="rounded-2xl overflow-hidden border border-amber-200 bg-gradient-to-br from-[#FFF8E7] via-[#FFF1D6] to-[#FFE7C2]" data-testid="rudramsha-hero">
              <div className="p-6 sm:p-8 grid grid-cols-1 md:grid-cols-[auto,1fr] gap-7 items-center">
                <div className="flex justify-center md:justify-start">
                  <div className="p-3 rounded-xl bg-white shadow-sm border border-amber-200">
                    <KundaliChart kundali={result.d11} size={300} />
                  </div>
                </div>
                <div>
                  <div className="label-eyebrow text-[#7C2D12] flex items-center gap-2">
                    <Network size={14} weight="duotone" /> D11 · Rudramsha · Labhamsha
                  </div>
                  <h2 className="font-serif-display text-3xl sm:text-4xl mt-3 font-light leading-tight text-[#3F2D1B]" data-testid="d11-lagna-title">
                    {result.d11_lagna_flavour.title} · {result.d11.lagna.rashi} lagna
                  </h2>
                  <div className="text-xs text-muted mt-2 font-medium">
                    Ruled by {result.d11.lagna.rashi_lord}
                  </div>
                  <p className="text-sm sm:text-base text-[#3F2D1B] mt-4 font-light leading-snug">
                    {result.d11_lagna_flavour.description}
                  </p>
                  <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    {Object.entries(result.d11.planets).map(([name, p]) => (
                      <div key={name} className="px-3 py-2 rounded-md bg-white/70 border border-amber-200" data-testid={`d11-legend-${name}`}>
                        <div className="font-medium text-[#3F2D1B]">{name}{p.retrograde && <span className="text-rose-700 ml-1">(R)</span>}</div>
                        <div className="text-muted">{p.rashi}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* === WEALTH AXIS · 2/5/9/11 === */}
            <section className="rounded-2xl p-5 sm:p-7 bg-white border border-soft" data-testid="wealth-axis">
              <div className="flex items-center gap-2 label-eyebrow text-[#7C2D12]">
                <ChartLineUp size={14} weight="duotone" /> Wealth-axis strength · D1 houses 2 · 5 · 9 · 11
              </div>
              <p className="text-xs text-muted mt-2 max-w-3xl">
                Material gain is not the only outcome of an active 11th. The Barbara Pijan rule: read ALL four finance-axis houses together — 2 hoards, 5 speculates, 9 blesses the long-game, 11 generates marketplace gains. Below is your strength score for each.
              </p>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                {["h2", "h5", "h9", "h11"].map((k) => {
                  const h = result.wealth_axis[k];
                  const tint = BAND_TINT[h.band] || BAND_TINT.moderate;
                  const label = { h2: "H2 · Hoard", h5: "H5 · Speculation", h9: "H9 · Fortune", h11: "H11 · Gains" }[k];
                  return (
                    <div key={k} className={`rounded-xl p-4 border ${tint.bg}`} data-testid={`wealth-axis-${k}`}>
                      <div className={`text-[10px] font-bold tracking-wider ${tint.text}`}>{label}</div>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className={`font-serif-display text-3xl ${tint.text}`}>{h.score}</span>
                        <span className="text-xs text-muted">/10</span>
                        <span className={`ml-auto text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${tint.chip}`}>
                          {h.band.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-xs text-muted mt-2">
                        Lord <b>{h.lord}</b> in H{h.lord_house} · <span className="capitalize">{h.lord_dignity.replace("_", " ")}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              {result.wealth_axis.archetypes.length > 0 && (
                <div className="mt-6 space-y-3" data-testid="archetypes">
                  <div className="label-eyebrow text-muted">Archetypes detected in your chart</div>
                  {result.wealth_axis.archetypes.map((a, i) => (
                    <div key={i} className="rounded-xl p-4 bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-300" data-testid={`archetype-${a.key}`}>
                      <div className="font-medium text-[#7C2D12] flex items-center gap-2">
                        <Crown size={14} weight="duotone" /> {a.title}
                      </div>
                      <p className="text-sm text-[#3F2D1B] mt-2 font-light leading-snug">{a.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* === 11TH HOUSE DEEP READ === */}
            <section className="rounded-2xl p-5 sm:p-7 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200" data-testid="eleventh-house">
              <div className="flex items-center gap-2 label-eyebrow text-[#7C2D12]">
                <Lightning size={14} weight="duotone" /> Your literal 11th house · the income engine
              </div>
              <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light text-[#3F2D1B]">
                {result.eleventh_house.sign} · ruled by {result.eleventh_house.lord}
              </h3>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl p-4 bg-white/60 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest text-muted">In D1</div>
                  <div className="font-medium text-[#3F2D1B] mt-1.5">
                    {result.eleventh_house.lord} in H{result.eleventh_house.lord_d1_house}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    Sign: {result.eleventh_house.lord_d1_sign} · <span className="capitalize">{result.eleventh_house.lord_d1_dignity.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="rounded-xl p-4 bg-white/60 border border-amber-200">
                  <div className="text-[10px] uppercase tracking-widest text-muted">In D11 (refined)</div>
                  <div className="font-medium text-[#3F2D1B] mt-1.5">
                    {result.eleventh_house.lord} in H{result.eleventh_house.lord_in_d11_house}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    Sign: {result.eleventh_house.lord_in_d11_sign} · <span className="capitalize">{(result.eleventh_house.lord_in_d11_dignity || "").replace("_", " ")}</span>
                  </div>
                </div>
              </div>
              {result.eleventh_house.occupants.length > 0 && (
                <div className="mt-5">
                  <div className="text-[10px] uppercase tracking-widest text-muted mb-2">Planets sitting in your 11th house (D1)</div>
                  <div className="space-y-2">
                    {result.eleventh_house.occupants.map((o, i) => (
                      <div key={i} className="rounded-lg p-3 bg-white/70 border border-amber-200 text-sm" data-testid={`occupant-${o.planet}`}>
                        <div className="font-medium text-[#3F2D1B]">
                          {o.planet}{o.retrograde && <span className="text-rose-700 ml-1 text-xs">(R)</span>}
                          <span className="ml-2 text-xs text-muted capitalize">· {o.dignity.replace("_", " ")} · {o.nakshatra}</span>
                        </div>
                        <div className="text-xs text-muted mt-1">{o.gain_flavour}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* === CURRENT DASHA × D11 === */}
            <section className="rounded-2xl p-5 sm:p-7 bg-gradient-to-br from-violet-50 to-fuchsia-50 border border-violet-200" data-testid="current-dasha">
              <div className="flex items-center gap-2 label-eyebrow text-violet-900">
                <Star size={14} weight="duotone" /> Your current Vimshottari × D11 reading
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl p-4 bg-white/70 border border-violet-200">
                  <div className="text-[10px] uppercase tracking-widest text-muted">Mahadasha</div>
                  <div className="font-serif-display text-2xl text-violet-950 mt-1">{result.current_dasha.md_lord}</div>
                  <div className="text-xs text-muted mt-2">
                    D11: {result.current_dasha.md_d11_sign} · H{result.current_dasha.md_d11_house} · <span className="capitalize">{(result.current_dasha.md_d11_dignity || "").replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-violet-950 mt-2 italic font-light">{result.current_dasha.md_gain_flavour}</div>
                </div>
                <div className="rounded-xl p-4 bg-white/70 border border-violet-200">
                  <div className="text-[10px] uppercase tracking-widest text-muted">Antardasha</div>
                  <div className="font-serif-display text-2xl text-violet-950 mt-1">{result.current_dasha.ad_lord}</div>
                  <div className="text-xs text-muted mt-2">
                    D11: {result.current_dasha.ad_d11_sign} · H{result.current_dasha.ad_d11_house} · <span className="capitalize">{(result.current_dasha.ad_d11_dignity || "").replace("_", " ")}</span>
                  </div>
                  <div className="text-xs text-violet-950 mt-2 italic font-light">{result.current_dasha.ad_gain_flavour}</div>
                </div>
              </div>
            </section>

            {/* === FULL D11 PLANET TABLE === */}
            <section className="space-y-3">
              <div className="label-eyebrow text-muted">All 9 planets · D1 → D11 placements</div>
              <div className="rounded-2xl overflow-hidden border border-soft">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#FAF4E8] text-xs text-muted">
                      <tr>
                        <th className="text-left p-3">Planet</th>
                        <th className="text-left p-3">D1 sign</th>
                        <th className="text-left p-3">D11 sign</th>
                        <th className="text-left p-3">H of D11</th>
                        <th className="text-left p-3">Dignity</th>
                        <th className="text-left p-3 hidden md:table-cell">Nakshatra · Pada</th>
                        <th className="text-left p-3 hidden lg:table-cell">Gain flavour</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.planet_rows.map((r) => {
                        const chip = DIGNITY_CHIP[r.dignity] || DIGNITY_CHIP.neutral;
                        const tint = PLANET_TINT[r.planet] || "amber";
                        return (
                          <tr key={r.planet} className="border-t border-soft" data-testid={`d11-planet-${r.planet}`}>
                            <td className={`p-3 font-medium text-${tint}-900`}>
                              {r.planet}{r.retrograde && <span className="ml-1.5 text-rose-700 text-xs">(R)</span>}
                              {r.vargottama && (
                                <span className="ml-2 text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded bg-amber-500 text-white">VARGOTTAMA</span>
                              )}
                            </td>
                            <td className="p-3 text-muted">{r.d1_rashi}</td>
                            <td className="p-3 font-medium">{r.d11_rashi}</td>
                            <td className="p-3">H{r.d11_house}</td>
                            <td className="p-3">
                              <span className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${chip.cls}`}>{chip.label}</span>
                            </td>
                            <td className="p-3 text-muted hidden md:table-cell">{r.natal_nakshatra} · {r.pada}</td>
                            <td className="p-3 text-xs text-muted hidden lg:table-cell">{r.gain_flavour}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              {result.vargottama_planets.length > 0 && (
                <p className="text-xs text-amber-900 mt-2 font-medium">
                  ★ Vargottama in D11: <b>{result.vargottama_planets.join(", ")}</b> — same sign in D1 AND D11 = strongest possible gains signature for this planet.
                </p>
              )}
            </section>

            {/* === CAVEAT === */}
            <section className="rounded-2xl p-5 sm:p-6 bg-stone-50 border border-stone-300" data-testid="caveat">
              <div className="flex items-center gap-2 label-eyebrow text-stone-700">
                <Warning size={14} weight="duotone" /> {result.caveat.title}
              </div>
              <p className="text-sm text-stone-800 mt-3 font-light leading-snug">{result.caveat.body}</p>
              <p className="text-xs text-stone-700 mt-3 italic leading-snug">{result.caveat.warning}</p>
            </section>

            {/* === PREMIUM · Claude-personalised narrative (inside the same gate as everything else above) === */}
            <PremiumNarrative
              // Derive from saved-report hydration too — eliminates the
              // race where PremiumNarrative's mount-effect fires Claude
              // again before our hydration setNarrative commits. On
              // /rudramsha?report=<id> the saved narrative is non-null
              // on first render, so the safety-net short-circuits and
              // we never POST /api/rudramsha/narrative on revisits.
              narrative={narrative || result?.narrative}
              narrativeBusy={narrativeBusy}
              fetchNarrative={fetchNarrative}
            />
              </div>

            {/* Astrologer CTA — sits OUTSIDE the gate so even unpaid users
                see the CTA to talk to a wealth astrologer (alternative path
                to value if they don't want to pay for the report). */}
            <ToolAstrologerCta
              requiredTechnique="Mundane + Business Astrology"
              pitch="Decode your full wealth blueprint — D11 + dasha overlay — with a wealth-astrology specialist"
              ctaLabel="Talk to"
            />
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}


function PremiumNarrative({ narrative, narrativeBusy, fetchNarrative }) {
  React.useEffect(() => {
    // Safety net: if for some reason the parent didn't pre-fetch (e.g.
    // user reloaded the page mid-flow), trigger the fetch here. The
    // normal path is: analyze() kicks the narrative in parallel with
    // the chart so by the time the paywall unlocks it's already here.
    if (!narrative && !narrativeBusy) fetchNarrative();
  }, []);

  if (narrativeBusy || !narrative) {
    return (
      <div
        className="rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-amber-50"
        data-testid="narrative-loading"
      >
        <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest">
          <Sparkle size={14} weight="fill" className="animate-pulse" />
          Crafting your personalised reading
        </div>
        <div className="mt-4 space-y-2">
          {/* Skeleton shimmer — looks like the real card filling in, much
              calmer than a literal 'Claude is loading…' message. */}
          <div className="h-3 rounded bg-amber-100/10 animate-pulse" style={{ width: "82%" }} />
          <div className="h-3 rounded bg-amber-100/10 animate-pulse" style={{ width: "94%" }} />
          <div className="h-3 rounded bg-amber-100/10 animate-pulse" style={{ width: "76%" }} />
          <div className="h-3 rounded bg-amber-100/10 animate-pulse" style={{ width: "88%" }} />
        </div>
      </div>
    );
  }

  const sections = [
    { key: "opening",           label: "Your D11 in plain English",      cls: "from-amber-100 to-yellow-50 border-amber-200" },
    { key: "d11_lagna_read",    label: "The D11 lagna lens",             cls: "from-emerald-50 to-teal-50 border-emerald-200" },
    { key: "income_engine",     label: "Your income engine",             cls: "from-rose-50 to-orange-50 border-rose-200" },
    { key: "h2_h5_h9_h11_read", label: "Wealth-axis synthesis",          cls: "from-fuchsia-50 to-pink-50 border-fuchsia-200" },
    { key: "current_dasha_read",label: "What's firing right now",        cls: "from-violet-50 to-indigo-50 border-violet-200" },
    { key: "wealth_blueprint",  label: "Your wealth blueprint",          cls: "from-amber-50 to-orange-50 border-amber-200" },
    { key: "ten_year_outlook",  label: "10-year outlook",                cls: "from-sky-50 to-cyan-50 border-sky-200" },
  ];

  return (
    <div className="space-y-4" data-testid="rudramsha-narrative">
      <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-[#3F2D1B] via-[#5a3e22] to-[#7c4f2a] text-amber-50">
        <div className="flex items-center gap-2 text-amber-300 text-[10px] uppercase tracking-widest">
          <Sparkle size={14} weight="fill" /> Your personalised deep Rudramsha reading
        </div>
        <h3 className="font-serif-display text-2xl sm:text-3xl mt-3 font-light leading-snug">
          Your full Rudramsha wealth narrative
        </h3>
      </div>

      {sections.map((s) => narrative[s.key] && (
        <div key={s.key} className={`rounded-2xl p-5 sm:p-6 bg-gradient-to-br ${s.cls} border`} data-testid={`narrative-${s.key}`}>
          <div className="label-eyebrow text-[#7C2D12]">{s.label}</div>
          <p className="text-sm sm:text-base mt-3 font-light leading-snug text-[#3F2D1B]">{narrative[s.key]}</p>
        </div>
      ))}

      {Array.isArray(narrative.what_to_pursue) && narrative.what_to_pursue.length > 0 && (
        <div className="rounded-2xl p-5 sm:p-6 bg-emerald-50 border border-emerald-200" data-testid="narrative-pursue">
          <div className="label-eyebrow text-emerald-900">Pursue</div>
          <ul className="mt-3 space-y-2">
            {narrative.what_to_pursue.map((it, i) => (
              <li key={i} className="text-sm text-emerald-950 font-light leading-snug flex gap-2">
                <span className="text-emerald-700 font-bold">→</span><span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(narrative.what_to_avoid) && narrative.what_to_avoid.length > 0 && (
        <div className="rounded-2xl p-5 sm:p-6 bg-rose-50 border border-rose-200" data-testid="narrative-avoid">
          <div className="label-eyebrow text-rose-900">Avoid</div>
          <ul className="mt-3 space-y-2">
            {narrative.what_to_avoid.map((it, i) => (
              <li key={i} className="text-sm text-rose-950 font-light leading-snug flex gap-2">
                <span className="text-rose-700 font-bold">⚠</span><span>{it}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {narrative.closing_blessing && (
        <div className="rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-[#FFF8E7] via-[#FFF1D6] to-[#FFE7C2] border border-amber-200" data-testid="narrative-closing">
          <Lock size={0} className="hidden" /> {/* avoid unused-import lint */}
          <p className="font-serif-display text-lg sm:text-xl text-[#3F2D1B] font-light leading-snug italic">
            {narrative.closing_blessing}
          </p>
        </div>
      )}
    </div>
  );
}
